<template>
  <div class="popup z-index-10-fg confirmation small-popup edit-reminder-popup" ref="popupWindow">
    <div class="popup-content" ref="popupContent">
      <div class="popup-header">
        <h2>{{ isEdit ? 'Edit' : 'Add' }} Reminder</h2>
        <img src="../assets/close.svg" alt="" class="popup-close" @click="$emit('closePopup')" />
      </div>
      <div class="popup-body">
        <Dropdown :options="userOptions" label="Assigned To" v-model="assignedTo" class="input-item medium-item" :invalid="assignedTo === '' && showErrors" :readonly="!user.contactsAdmin" />

        <TextBox v-model="date" label="Date" type="date" class="input-item medium-item" :invalid="date === '' && showErrors" :required="true" />

        <Dropdown :options="typeOptions" label="Type" v-model="type" class="input-item medium-item" :invalid="type === '' && showErrors" />

        <TextArea v-model="reminderText" label="Reminder Text" :rows="6" :invalid="reminderText === '' && showErrors" :required="true" class="input-item large-item" />

        <Checkbox v-model="completed" label="Complete" class="input-item small-item" v-if="isEdit" />
      </div>
      <div class="popup-footer">
        <button @click="$emit('closePopup')" class="text-btn">Close</button>
        <button :class="{ 'square-btn': true, saving: isSaving }" @click="saveReminder()">Save<img src="../assets/spinner.svg" /></button>
      </div>
    </div>
  </div>
  <div class="screen-overlay-gray z-index-10-bg"></div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import Dropdown from './inputs/Dropdown.vue';
import TextBox from './inputs/TextBox.vue';
import TextArea from './inputs/TextArea.vue';
import Checkbox from './inputs/Checkbox.vue';
import useAlerts from '@/composables/useAlerts';
import usePicklistItemsQuery from '@/composables/usePicklistItemsQuery';
import useUsersQuery from '@/composables/useUsersQuery';
import useCurrentUser from '@/composables/useCurrentUser';
import useReminderMutate from '@/composables/useReminderMutate';

export default {
  name: 'EditReminder',
  emits: ['closePopup', 'save'],
  components: {
    Dropdown,
    TextBox,
    TextArea,
    Checkbox,
  },
  props: {
    reminderEntry: {
      type: Object,
      default: null,
    },
    entityType: {
      type: String,
      required: true,
      validator: (value) => ['company', 'contact'].includes(value),
    },
    entityId: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { addAlert } = useAlerts();
    const { user } = useCurrentUser();
    const { addReminder, updateReminder } = useReminderMutate();
    const { allActiveUsers, getUsers } = useUsersQuery();

    getUsers();

    // Reactive data
    const assignedTo = ref('');
    const date = ref('');
    const type = ref('');
    const completed = ref(false);
    const reminderText = ref('');
    const isSaving = ref(false);
    const showErrors = ref(false);

    // Computed properties
    const isEdit = computed(() => {
      return props.reminderEntry && props.reminderEntry.REMINDERID;
    });

    const typeOptions = computed(() => {
      return usePicklistItemsQuery.pickLists.reminderTypes.items.map((item) => item.NAME);
    });

    const userOptions = computed(() => {
      return allActiveUsers.map((user) => ({
        value: user.USERID,
        text: user.DISPLAYNAME || 'Unknown User',
      }));
    });

    const canSave = computed(() => {
      const hasAssignedTo = assignedTo.value !== '';
      const hasDate = date.value !== '';
      const hasType = type.value !== '';
      const hasReminderText = reminderText.value.trim() !== '';

      return hasAssignedTo && hasDate && hasType && hasReminderText;
    });

    // Initialize form data
    const initializeForm = () => {
      if (props.reminderEntry) {
        // Editing existing reminder
        assignedTo.value = props.reminderEntry.ASSIGNEDID || '';
        date.value = formatDateForInput(props.reminderEntry.DATE) || '';
        type.value = props.reminderEntry.TYPE || '';
        completed.value = props.reminderEntry.COMPLETED || false;
        reminderText.value = props.reminderEntry.TEXT || '';
      } else {
        // Adding new reminder - clear all fields and set defaults
        assignedTo.value = user.userid || '';
        date.value = '';
        type.value = '';
        completed.value = false;
        reminderText.value = '';
      }
    };

    // Helper function to format date for input field
    const formatDateForInput = (dateString) => {
      if (!dateString) return '';

      try {
        // Try to parse as integer timestamp first
        const timestamp = parseInt(dateString);
        if (!isNaN(timestamp)) {
          const date = new Date(timestamp);
          return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        }

        // Fall back to string parsing
        const date = new Date(dateString);
        return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      } catch (error) {
        console.error('Error parsing date:', error);
        return '';
      }
    };

    // Helper function to convert date input to timestamp
    const convertDateToTimestamp = (dateString) => {
      if (!dateString) return '';

      try {
        const date = new Date(dateString);
        return date.getTime().toString();
      } catch (error) {
        console.error('Error converting date:', error);
        return '';
      }
    };

    // Save function
    const saveReminder = async () => {
      if (!canSave.value) {
        showErrors.value = true;
        addAlert(`Please fill in all required fields`, 'high', 3);
        return;
      } else {
        showErrors.value = false;
      }

      isSaving.value = true;

      try {
        // Prepare the reminder data
        const reminderData = {
          ASSIGNEDID: assignedTo.value,
          DATE: convertDateToTimestamp(date.value),
          TYPE: type.value,
          COMPLETED: completed.value,
          TEXT: reminderText.value.trim(),
        };

        // Add entity-specific fields
        if (props.entityType === 'company') {
          reminderData.COMPANYID = props.entityId;
          reminderData.CONTACTID = null;
        } else if (props.entityType === 'contact') {
          reminderData.CONTACTID = props.entityId;
          reminderData.COMPANYID = null;
        }

        // Call appropriate mutation
        if (isEdit.value) {
          await updateReminder(props.reminderEntry.REMINDERID, reminderData);
        } else {
          await addReminder(reminderData);
        }

        // Show success message
        addAlert(isEdit.value ? 'Reminder updated successfully' : 'Reminder added successfully', 'low', 2);

        // Emit save event for parent component to refresh data
        emit('save');

        // Close the popup
        emit('closePopup');
      } catch (error) {
        console.error('Error saving reminder:', error);
        addAlert('Error saving reminder', 'high', 5);
      } finally {
        isSaving.value = false;
      }
    };

    // Watch for reminder entry changes to reinitialize form
    watch(() => props.reminderEntry, initializeForm, { immediate: true });

    return {
      assignedTo,
      date,
      type,
      completed,
      reminderText,
      isSaving,
      isEdit,
      typeOptions,
      userOptions,
      canSave,
      saveReminder,
      addAlert,
      showErrors,
      user,
    };
  },
};
</script>

<style lang="scss" scoped>
.edit-reminder-popup.small-popup {
  width: 50rem;

  .popup-body {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1.5rem 1rem;
    margin-bottom: 1.5rem;

    .input-item {
      min-width: 20rem;
      flex: 1 0 45%;

      &.small-item {
        min-width: 5rem;
        flex: 1 0 10%;
      }

      &.large-item {
        min-width: 20rem;
        flex: 1 0 90%;
      }

      &.medium-item {
        min-width: 10rem;
        flex: 1 0 30%;
      }
    }
  }
}
</style>
